import React from "react";
import { Modal, Button, Table, Alert } from "antd";
import { CloseOutlined, DownloadOutlined, ExclamationCircleOutlined, CheckCircleOutlined } from "@ant-design/icons";
import "./CSVFormatModal.scss";

const CSVFormatModal = ({ visible, onCancel }) => {
  const columns = [
    {
      title: "",
      dataIndex: "index",
      key: "index",
      width: 40,
      render: (text) => <span className="column-index">{text}.</span>
    },
    {
      title: "Column",
      dataIndex: "column",
      key: "column",
      width: 120,
      render: (text) => <span className="column-name">{text}</span>
    },
    {
      title: "Example",
      dataIndex: "example",
      key: "example",
      width: 150,
      render: (text, record) => (
        <span className={`example-value ${record.type}`}>{text}</span>
      )
    },
    {
      title: "Info",
      dataIndex: "info",
      key: "info",
      render: (text) => <span className="column-info">{text}</span>
    },
    {
      title: "Mandatory",
      dataIndex: "mandatory",
      key: "mandatory",
      width: 100,
      align: "center",
      render: (mandatory) => (
        mandatory ? (
          <CheckCircleOutlined className="mandatory-icon required" />
        ) : (
          <CloseOutlined className="mandatory-icon optional" />
        )
      )
    }
  ];

  const data = [
    {
      key: "1",
      index: 1,
      column: "Pincode",
      example: "411001",
      info: "Pincode",
      mandatory: true,
      type: "required"
    },
    {
      key: "2",
      index: 2,
      column: "State_Code",
      example: "MH",
      info: "State code of the pincode state (for validation)",
      mandatory: false,
      type: "optional"
    },
    {
      key: "3",
      index: 3,
      column: "Hub_ID",
      example: "12345678900AHD",
      info: "Hub assigned to pincode",
      mandatory: false,
      type: "optional"
    },
    {
      key: "4",
      index: 4,
      column: "Hub_Status",
      example: "Yes/ No",
      info: "\"Yes\" for activating and \"No\" for deactivating",
      mandatory: false,
      type: "optional"
    },
    {
      key: "5",
      index: 5,
      column: "DP_ID",
      example: "JDL1234567890",
      info: "Delivery Partner assigned to pincode",
      mandatory: false,
      type: "optional"
    },
    {
      key: "6",
      index: 6,
      column: "DP_Type",
      example: "Hub_DP / 3PL",
      info: "\"Hub_DP\" for Delivery Partner associated with hub & 3PL for individual Delivery Partners",
      mandatory: false,
      type: "optional"
    },
    {
      key: "7",
      index: 7,
      column: "DP_Hub_ID",
      example: "12345678900AHD",
      info: "If DP_Type is \"Hub_DP\", DP_ID of the associated hub",
      mandatory: false,
      type: "optional"
    },
    {
      key: "8",
      index: 8,
      column: "Services",
      example: "FM, LM, To_Pay",
      info: "Service code of available services with Delivery Partner (eg: FM, LM, To_Pay, COD, SDD, NDD, AD)",
      mandatory: false,
      type: "optional"
    },
    {
      key: "9",
      index: 9,
      column: "Pincode_Status",
      example: "Yes/ No",
      info: "\"Yes\" for activating and \"No\" for deactivating",
      mandatory: true,
      type: "required"
    }
  ];

  const handleDownloadSample = () => {
    // Create CSV content
    const csvContent = [
      "Pincode,State_Code,Hub_ID,Hub_Status,DP_ID,DP_Type,DP_Hub_ID,Services,Pincode_Status",
      "411001,MH,12345678900AHD,Yes,JDL1234567890,Hub_DP,12345678900AHD,\"FM,LM,To_Pay\",Yes",
      "400001,MH,12345678900MUM,Yes,JDL1234567891,3PL,,\"FM,LM,COD,SDD\",Yes",
      "560001,KA,12345678900BLR,No,JDL1234567892,Hub_DP,12345678900BLR,\"FM,LM,NDD,AD\",No"
    ].join("\n");

    // Create and download file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "pincode_sample.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Modal
      title={
        <div className="csv-modal-header">
          <span className="csv-modal-title">
            Upload a CSV file to bulk import or update pincode configurations. The file should contain columns:
          </span>
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={onCancel}
            className="csv-close-btn"
          />
        </div>
      }
      visible={visible}
      footer={null}
      width={800}
      className="csv-format-modal"
      closeIcon={null}
      closable={false}
    >
      <div className="csv-modal-content">
        <div className="csv-table-container">
          <Table
            columns={columns}
            dataSource={data}
            pagination={false}
            className="csv-format-table"
            size="small"
          />
        </div>

        <div className="csv-modal-footer">
          <Alert
            message="For a pincode if both Hub_ID & DP_ID are empty, pincode will not be added."
            type="warning"
            icon={<ExclamationCircleOutlined />}
            className="csv-warning"
            showIcon
          />
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownloadSample}
            className="download-sample-btn"
          >
            Download Sample CSV
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CSVFormatModal;
